import { Alert, Box, Button, Card, Heading, Paragraph, Popover } from '@zillow/constellation-10';
import { IconDownloadOutline } from '@zillow/constellation-icons';
import { useContext, useMemo } from 'react';
import { useAuth } from '../../AuthProvider';
import { useSatelliteWebLandlordPropertyHandler_getListingPerformance } from '@zg-rentals/ts-satellite-client-react-query';
import { MultiFamilyLeadsPageContext } from '../../contexts/MultiFamilyLeadsContextProvider';
import { getDateRangesFromDaysBackOrStartDate } from '../../utils/timeFrameDropdownUtils';
import { DEFAULT_DAYS_BACK } from '../TimeFrameDropdown';
import { Skeleton } from './Skeleton';
import getExcelDownloadUrl from '../../utils/getExcelDownloadUrl';
import { AppContext } from '../../contexts/AppContextProvider';
import {
  emitExportLeadsClickEvent,
  EXPORT_LEADS_CLICK_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/multiFamilyLeadsPage';

const LeadSummaryCounts = () => {
  const { paths } = useContext(AppContext);
  const { listingAlias, daysBack, startDate } = useContext(MultiFamilyLeadsPageContext);
  const { user } = useAuth();
  const { isEnterpriseCustomer } = user || {};

  const daysBackArg = daysBack ? parseInt(daysBack) : DEFAULT_DAYS_BACK;
  const { startDate: startDateFormatted, endDate: endDateFormatted } = getDateRangesFromDaysBackOrStartDate(
    daysBackArg,
    startDate,
  );

  const postData = useMemo(
    () => ({
      startDayYyyyMmDd: startDateFormatted,
      endDayYyyyMmDd: endDateFormatted,
      includeMarketInsights: false,
      listingAlias: listingAlias ?? undefined,
    }),
    [startDateFormatted, endDateFormatted, listingAlias],
  );

  const {
    data: listingPerformanceData,
    isLoading,
    error: listingPerformanceError,
  } = useSatelliteWebLandlordPropertyHandler_getListingPerformance(postData, {
    meta: {
      context: {
        api: 'getListingPerformance',
        details: 'LeadSummaryCounts - Error fetching counts for leads page contacts summary',
      },
    },
  });

  const clickCount = Math.max(0, listingPerformanceData?.listingWebReferrals ?? 0);
  const leadCount = Math.max(0, listingPerformanceData?.leadCount ?? 0);
  const totalLeadsCount = useMemo(() => clickCount + leadCount, [clickCount, leadCount]);
  const formattedClickCount = clickCount.toLocaleString();
  const formattedLeadCount = leadCount.toLocaleString();
  const formattedTotalLeadsCount = totalLeadsCount.toLocaleString();

  // The BE returns aggregated counts (per-listing or portfolio-wide); when everything is zero
  // there's nothing useful to show, so hide the summary rather than render an empty 0/0/0 card.
  const showContactsSummary = !!listingPerformanceData && totalLeadsCount > 0;

  const summaryCountsForNonEnterpriseCustomers = useMemo(
    () => [
      {
        title: 'Total contacts',
        count: formattedTotalLeadsCount,
        tooltipContent: null,
      },
      {
        title: 'Total leads',
        count: formattedLeadCount,
        tooltipContent: null,
      },
      {
        title: 'Total website referrals',
        count: formattedClickCount,
        tooltipContent: null,
      },
    ],
    [formattedTotalLeadsCount, formattedLeadCount, formattedClickCount],
  );

  const summaryCountsForEnterpriseCustomers = useMemo(
    () => [
      {
        title: 'Total prospects',
        count: formattedTotalLeadsCount,
        tooltipContent: {
          heading: 'Total prospects',
          body: 'The combined total of your direct leads and website referrals from Zillow Rentals Network.',
        },
      },
      {
        title: 'Total direct leads',
        count: formattedLeadCount,
        tooltipContent: {
          heading: 'Total direct leads',
          body: 'Leads that called, messaged, or requested a tour through Zillow Rentals Network.',
        },
      },
      {
        title: 'Total website referrals',
        count: formattedClickCount,
        tooltipContent: {
          heading: 'Total website referrals',
          body: "All traffic to your properties' websites through Zillow Rentals Network.",
        },
      },
    ],
    [formattedTotalLeadsCount, formattedLeadCount, formattedClickCount],
  );

  const excelDownloadUrl = useMemo(
    () =>
      getExcelDownloadUrl({
        baseUrl: paths.homePage,
        excelPath: listingAlias ? paths.exportExcel.leadsForListing : paths.exportExcel.leadsForUser,
        listingAlias,
        startDateYyyyMmDd: startDateFormatted,
        endDateYyyyMmDd: endDateFormatted,
      }),
    [endDateFormatted, listingAlias, startDateFormatted],
  );

  const handleExportLeadsClick = () => {
    emitExportLeadsClickEvent(user);
  };

  // The /getListingPerformance endpoint only returns data for promoted listings
  // We hide the summary counts for non-promoted listings
  const shouldHideSection = !showContactsSummary && !isLoading && !listingPerformanceError;
  if (shouldHideSection) return null;

  const summaryCounts = isEnterpriseCustomer
    ? summaryCountsForEnterpriseCustomers
    : summaryCountsForNonEnterpriseCustomers;

  return (
    <Card
      elevated={false}
      css={{
        marginTop: 'loose',
        paddingInline: 'looser',
        paddingBlock: 'loose',
        display: 'flex',
        flexDirection: 'row',
        xl: { alignItems: 'center' },
        xlDown: { flexDirection: 'column' },
      }}
    >
      {!isEnterpriseCustomer && (
        <Box css={{ display: 'flex', flexDirection: 'column', xl: { marginRight: '96px' } }}>
          <Heading level={2} textStyle="heading-xs">
            Contacts
          </Heading>
          <Paragraph textStyle="body" css={{ marginTop: 'tighter' }}>
            Contacts are a combination of leads and website referrals
          </Paragraph>
        </Box>
      )}

      <Box css={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box
          css={{
            xlDown: { marginTop: 'default' },
            xl: { marginLeft: 'auto' },
            ...(isEnterpriseCustomer && { lg: { marginLeft: 'auto' }, xlDown: { marginTop: 0 } }),
          }}
        >
          <Button
            data-eventid={EXPORT_LEADS_CLICK_EVENT_TYPE_ID}
            asChild
            emphasis="outlined"
            size="sm"
            tone="neutral"
            icon={<IconDownloadOutline />}
            iconPosition="end"
            onClick={handleExportLeadsClick}
          >
            <a href={excelDownloadUrl}>Export leads</a>
          </Button>
        </Box>

        {listingPerformanceError ? (
          <Alert tone="critical" css={{ marginTop: 'default' }}>
            We couldn&apos;t load contact counts. Please refresh the page and try again.
          </Alert>
        ) : (
          <Box
            css={{
              lgDown: { flexDirection: 'column' },
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
            }}
          >
            {summaryCounts.map(({ title, count, tooltipContent }) => (
              <Box
                key={title}
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  marginTop: 'default',
                  lg: { alignItems: 'flex-end' },
                  xl: { paddingInline: 'tighter' },
                }}
              >
                <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 'tighter' }}>
                  <Paragraph css={{ color: 'text.subtle', whiteSpace: 'nowrap' }}>{title}</Paragraph>
                  {tooltipContent && (
                    <Popover
                      body={<Paragraph>{tooltipContent.body}</Paragraph>}
                      dividers
                      header={<Heading level={4}>{tooltipContent.heading}</Heading>}
                      shouldAwaitInteractionResponse
                      tone="neutral"
                    />
                  )}
                </Box>
                {isLoading ? (
                  <Skeleton />
                ) : (
                  <Heading level={2} textStyle="heading-md" fontFamily="heading-alt">
                    {count}
                  </Heading>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default LeadSummaryCounts;
